import { useState, useEffect, useRef } from 'react';
import { Folder, FileText, Upload, Download, Eye, X, Search, Trash2, ChevronRight, Home, FolderPlus, Image as ImageIcon, File, Loader2, Lock, CheckSquare, Square, List, LayoutGrid } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAllRows';
import { useAuth } from '../../contexts/AuthContext';

// Document Image Component with Signed URL
function DocumentImage({ doc, className }: { doc: Document; className?: string }) {
  const [imageUrl, setImageUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const getSignedUrl = async () => {
      try {
        setLoading(true);
        const isExternalUrl = doc.file_path.startsWith('http://') || doc.file_path.startsWith('https://');
        
        if (isExternalUrl) {
          setImageUrl(doc.file_path);
        } else {
          const { data, error } = await supabase.storage
            .from('company-documents')
            .createSignedUrl(doc.file_path, 3600); // 1 hour
          
          if (error) throw error;
          if (data?.signedUrl) {
            setImageUrl(data.signedUrl);
          }
        }
      } catch (err) {
        console.error('Error getting signed URL:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    getSignedUrl();
  }, [doc.file_path]);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400">
        <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
        <circle cx="9" cy="9" r="2"/>
        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
      </svg>
    );
  }

  return (
    <img 
      src={imageUrl} 
      alt={doc.name}
      className={className}
      onError={() => setError(true)}
    />
  );
}

// PDF Thumbnail Component
function PDFThumbnail({ doc }: { doc: Document }) {
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getPdfUrl = async () => {
      try {
        const isExternalUrl = doc.file_path.startsWith('http://') || doc.file_path.startsWith('https://');
        
        if (isExternalUrl) {
          setPdfUrl(doc.file_path);
        } else {
          const { data, error } = await supabase.storage
            .from('company-documents')
            .createSignedUrl(doc.file_path, 3600);
          
          if (error) throw error;
          if (data?.signedUrl) {
            setPdfUrl(data.signedUrl);
          }
        }
      } catch (err) {
        console.error('Error getting PDF URL:', err);
      } finally {
        setLoading(false);
      }
    };

    getPdfUrl();
  }, [doc.file_path]);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-red-400 animate-spin" />
      </div>
    );
  }

  if (!pdfUrl) {
    return (
      <div className="relative w-full h-full flex items-center justify-center">
        <FileText className="w-8 h-8 text-red-600" />
        <span className="absolute bottom-1 text-[10px] font-bold text-red-600">PDF</span>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full overflow-hidden">
      <iframe
        src={`${pdfUrl}#page=1&view=FitH&toolbar=0&navpanes=0&scrollbar=0`}
        className="w-full h-full pointer-events-none scale-150 origin-top-left"
        style={{ transform: 'scale(1.5) translateY(-10%)' }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-red-50 pointer-events-none" />
      <span className="absolute bottom-1 right-1 text-[10px] font-bold text-red-600 bg-white px-1 rounded">PDF</span>
    </div>
  );
}

interface DocumentFolder {
  id: string;
  name: string;
  parent_folder_id: string | null;
  is_system_folder?: boolean;
  is_ai_rate_conf_folder?: boolean;
  editable_by_roles?: string[] | null;
  dispatch_id?: string | null;
  user_id?: string | null;
  is_accessible?: boolean;
  created_at: string;
}

interface Document {
  id: string;
  name: string;
  file_path: string;
  file_size: number;
  file_type: string;
  mime_type: string | null;
  folder_id: string | null;
  created_at: string;
}

interface BreadcrumbItem {
  id: string | null;
  name: string;
}

export function DocumentsView() {
  const { profile } = useAuth();
  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [currentFolderData, setCurrentFolderData] = useState<DocumentFolder | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([{ id: null, name: 'My Documents' }]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchResults, setSearchResults] = useState<{folders: (DocumentFolder & {path: string})[], documents: (Document & {path: string})[]}>({folders: [], documents: []});
  const [isSearching, setIsSearching] = useState(false);
  
  // Bulk selection state
  const [selectedFolders, setSelectedFolders] = useState<Set<string>>(new Set());
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveTargetFolderId, setMoveTargetFolderId] = useState<string | null>(null);
  const [availableFolders, setAvailableFolders] = useState<DocumentFolder[]>([]);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    // Load saved view mode from localStorage
    const saved = localStorage.getItem('documentsViewMode');
    return (saved === 'grid' || saved === 'list') ? saved : 'grid';
  });
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Save view mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('documentsViewMode', viewMode);
  }, [viewMode]);

  // Load folder from URL hash on mount
  useEffect(() => {
    const hash = window.location.hash;
    const path = hash.replace('#/documents', '');
    if (path && path !== '' && path !== '/') {
      loadFolderFromPath(path);
    } else {
      loadFoldersAndDocuments();
    }
  }, []);

  useEffect(() => {
    loadFoldersAndDocuments();
  }, [currentFolderId]);

  const loadFoldersAndDocuments = async () => {
    setLoading(true);
    try {
      // Load folders - use .is() for NULL comparison
      let foldersQuery = supabase
        .from('document_folders')
        .select('*');
      
      if (currentFolderId === null) {
        foldersQuery = foldersQuery.is('parent_folder_id', null);
      } else {
        foldersQuery = foldersQuery.eq('parent_folder_id', currentFolderId);
      }
      
      const { data: foldersData, error: foldersError } = await foldersQuery.order('name');

      if (foldersError) throw foldersError;
      
      // Filter folders based on user role
      let filteredFolders = foldersData || [];
      
      // Dispatch users can only see "VOZACI KONFIRMACIJE - 2026" folder at root level
      if (profile?.role === 'Dispatch' && currentFolderId === null) {
        filteredFolders = filteredFolders.filter(folder => 
          folder.name === 'VOZACI KONFIRMACIJE - 2026'
        );
      } 
      // Show LOADS folder to all users so they can see AI-processed rate confirmations
      // Only filter out AI RATE CONFIRMATIONS folder for non-superadmin users
      else if (profile?.role !== 'Superadmin') {
        filteredFolders = filteredFolders.filter(folder => 
          folder.name !== 'AI RATE CONFIRMATIONS'
        );
      }
      
      setFolders(filteredFolders);

      // Load documents - use .is() for NULL comparison
      const documentsData = await fetchAllRows<any>(() => {
        let documentsQuery = supabase
          .from('documents')
          .select('*');

        if (currentFolderId === null) {
          documentsQuery = documentsQuery.is('folder_id', null);
        } else {
          documentsQuery = documentsQuery.eq('folder_id', currentFolderId);
        }

        return documentsQuery.order('created_at', { ascending: false });
      });

      setDocuments(documentsData || []);
    } catch (error) {
      console.error('Error loading folders and documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFolderFromPath = async (urlPath: string) => {
    // Parse URL path like /Dispatches/DIS-12345
    const pathParts = urlPath.split('/').filter(p => p);
    if (pathParts.length === 0) {
      loadFoldersAndDocuments();
      return;
    }

    // Navigate through folders by name
    let currentParentId: string | null = null;
    const breadcrumbPath: BreadcrumbItem[] = [{ id: null, name: 'My Documents' }];

    for (const folderName of pathParts) {
      const { data } = await supabase
        .from('document_folders')
        .select('id, name')
        .eq('name', decodeURIComponent(folderName))
        .eq('parent_folder_id', currentParentId)
        .single();

      if (data) {
        breadcrumbPath.push({ id: data.id, name: data.name });
        currentParentId = data.id;
      } else {
        break;
      }
    }

    setBreadcrumbs(breadcrumbPath);
    setCurrentFolderId(currentParentId);
  };

  const navigateToFolder = async (folderId: string | null, _folderName: string) => {
    setCurrentFolderId(folderId);
    
    if (folderId === null) {
      setBreadcrumbs([{ id: null, name: 'My Documents' }]);
      setCurrentFolderData(null);
      window.history.replaceState({}, '', '/#/documents');
    } else {
      // Load current folder data
      const { data: currentFolder } = await supabase
        .from('document_folders')
        .select('*')
        .eq('id', folderId)
        .single();
      
      setCurrentFolderData(currentFolder);
      
      // Build breadcrumb path
      const newBreadcrumbs: BreadcrumbItem[] = [{ id: null, name: 'My Documents' }];
      let currentId: string | null = folderId;
      const path: BreadcrumbItem[] = [];
      
      // Traverse up to build path
      while (currentId) {
        const { data: folderData } = await supabase
          .from('document_folders')
          .select('id, name, parent_folder_id')
          .eq('id', currentId)
          .single();
        
        if (folderData) {
          path.unshift({ id: folderData.id, name: folderData.name });
          currentId = folderData.parent_folder_id;
        } else {
          break;
        }
      }
      
      setBreadcrumbs([...newBreadcrumbs, ...path]);
      
      // Update URL hash with folder path using pushState to avoid page reload
      const urlPath = path.map(p => encodeURIComponent(p.name)).join('/');
      window.history.replaceState({}, '', `/#/documents/${urlPath}`);
    }
  };

  const deleteFolder = async (folderId: string, isSystemFolder?: boolean) => {
    // Afterhours role cannot delete anything
    if (profile?.role === 'Afterhours') {
      alert('You do not have permission to delete documents.');
      return;
    }
    
    // Check if this is the protected "VOZACI KONFIRMACIJE - 2025" folder
    const folder = folders.find(f => f.id === folderId);
    if (folder?.name === 'VOZACI KONFIRMACIJE - 2025') {
      if (profile?.role !== 'Admin' && profile?.role !== 'Superadmin') {
        alert('Only Admin and Superadmin can delete this folder.');
        return;
      }
    }
    
    // Allow superadmin to delete any folder, including system folders
    if (isSystemFolder && profile?.role !== 'Superadmin') {
      alert('This is a system folder and cannot be deleted.');
      return;
    }
    
    if (!confirm('Are you sure you want to delete this folder? All contents will be deleted.')) return;
    
    try {
      const { error } = await supabase
        .from('document_folders')
        .delete()
        .eq('id', folderId);
      
      if (error) throw error;
      loadFoldersAndDocuments();
    } catch (error) {
      console.error('Error deleting folder:', error);
      alert('Failed to delete folder');
    }
  };

  const deleteDocument = async (doc: Document) => {
    // Afterhours role cannot delete anything
    if (profile?.role === 'Afterhours') {
      alert('You do not have permission to delete documents.');
      return;
    }
    
    if (!confirm(`Are you sure you want to delete "${doc.name}"?`)) return;
    
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('company-documents')
        .remove([doc.file_path]);
      
      if (storageError) throw storageError;
      
      // Delete from database
      const { error: dbError } = await supabase
        .from('documents')
        .delete()
        .eq('id', doc.id);
      
      if (dbError) throw dbError;
      loadFoldersAndDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  const handleBulkDelete = async () => {
    // Afterhours role cannot delete anything
    if (profile?.role === 'Afterhours') {
      alert('You do not have permission to delete documents.');
      return;
    }
    
    const folderCount = selectedFolders.size;
    const documentCount = selectedDocuments.size;
    const totalCount = folderCount + documentCount;

    if (totalCount === 0) return;

    try {
      // Delete selected documents
      if (documentCount > 0) {
        const docsToDelete = documents.filter(doc => selectedDocuments.has(doc.id));
        const filePaths = docsToDelete.map(doc => doc.file_path);
        
        // Delete from storage
        const { error: storageError } = await supabase.storage
          .from('company-documents')
          .remove(filePaths);
        
        if (storageError) throw storageError;
        
        // Delete from database
        const { error: dbError } = await supabase
          .from('documents')
          .delete()
          .in('id', Array.from(selectedDocuments));
        
        if (dbError) throw dbError;
      }

      // Delete selected folders
      if (folderCount > 0) {
        for (const folderId of selectedFolders) {
          const folder = folders.find(f => f.id === folderId);
          // Allow superadmin to delete system folders
          if (folder && (!folder.is_system_folder || profile?.role === 'Superadmin')) {
            await deleteFolder(folderId, folder.is_system_folder);
          }
        }
      }

      // Clear selections and reload
      setSelectedFolders(new Set());
      setSelectedDocuments(new Set());
      setShowBulkDeleteConfirm(false);
      loadFoldersAndDocuments();
      alert(`Successfully deleted ${totalCount} item(s)`);
    } catch (error) {
      console.error('Error bulk deleting:', error);
      alert('Failed to delete some items');
    }
  };

  const toggleSelectAll = () => {
    if (selectedFolders.size === folders.length && selectedDocuments.size === documents.length) {
      // Deselect all
      setSelectedFolders(new Set());
      setSelectedDocuments(new Set());
    } else {
      // Select all
      setSelectedFolders(new Set(folders.map(f => f.id)));
      setSelectedDocuments(new Set(documents.map(d => d.id)));
    }
  };

  const loadAllFolders = async () => {
    try {
      const data = await fetchAllRows<any>(() => supabase
        .from('document_folders')
        .select('*')
        .order('name'));

      setAvailableFolders(data || []);
    } catch (error) {
      console.error('Error loading folders:', error);
    }
  };

  const handleBulkMove = async () => {
    if (!moveTargetFolderId) {
      alert('Please select a destination folder');
      return;
    }

    try {
      // Move selected documents
      if (selectedDocuments.size > 0) {
        const { error } = await supabase
          .from('documents')
          .update({ folder_id: moveTargetFolderId === 'root' ? null : moveTargetFolderId })
          .in('id', Array.from(selectedDocuments));
        
        if (error) throw error;
      }

      // Move selected folders
      if (selectedFolders.size > 0) {
        const { error } = await supabase
          .from('document_folders')
          .update({ parent_folder_id: moveTargetFolderId === 'root' ? null : moveTargetFolderId })
          .in('id', Array.from(selectedFolders));
        
        if (error) throw error;
      }

      // Clear selections and reload
      setSelectedFolders(new Set());
      setSelectedDocuments(new Set());
      setShowMoveModal(false);
      setMoveTargetFolderId(null);
      loadFoldersAndDocuments();
      alert(`Successfully moved ${selectedFolders.size + selectedDocuments.size} item(s)`);
    } catch (error) {
      console.error('Error moving items:', error);
      alert('Failed to move some items');
    }
  };

  const downloadDocument = async (doc: Document) => {
    try {
      const { data, error } = await supabase.storage
        .from('company-documents')
        .download(doc.file_path);
      
      if (error) throw error;
      
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    }
  };

  const handleMainAreaDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleMainAreaDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if leaving the main container
    if (e.currentTarget === e.target) {
      setIsDraggingOver(false);
    }
  };

  const handleMainAreaDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleMainAreaDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Upload files directly
    try {
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = currentFolderId ? `${currentFolderId}/${fileName}` : fileName;

        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from('company-documents')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        // Save metadata to database
        const { error: dbError } = await supabase.from('documents').insert({
          name: file.name,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          file_type: fileExt || 'unknown',
          mime_type: file.type,
          folder_id: currentFolderId,
          uploaded_by: profile?.id,
        });

        if (dbError) throw dbError;
      }

      loadFoldersAndDocuments();
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload some files');
    }
  };

  const previewDoc = (doc: Document) => {
    setPreviewDocument(doc);
    setShowPreview(true);
  };

  const getFileIcon = (mimeType: string | null) => {
    if (!mimeType) return FileText;
    if (mimeType.startsWith('image/')) return ImageIcon;
    if (mimeType === 'application/pdf') return FileText;
    return File;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const searchGlobally = async (term: string) => {
    if (!term.trim()) {
      setSearchResults({folders: [], documents: []});
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      // Search all folders
      const allFolders = await fetchAllRows<any>(() => supabase
        .from('document_folders')
        .select('*')
        .ilike('name', `%${term}%`));

      // Search all documents
      const allDocuments = await fetchAllRows<any>(() => supabase
        .from('documents')
        .select('*')
        .ilike('name', `%${term}%`));

      // Build paths for folders
      const foldersWithPaths = await Promise.all((allFolders || []).map(async (folder) => {
        const path = await buildFolderPath(folder.id);
        return { ...folder, path };
      }));

      // Build paths for documents
      const documentsWithPaths = await Promise.all((allDocuments || []).map(async (doc) => {
        const path = doc.folder_id ? await buildFolderPath(doc.folder_id) : 'Root';
        return { ...doc, path };
      }));

      setSearchResults({ folders: foldersWithPaths, documents: documentsWithPaths });
    } catch (error) {
      console.error('Error searching:', error);
    }
  };

  const buildFolderPath = async (folderId: string): Promise<string> => {
    const pathParts: string[] = [];
    let currentId: string | null = folderId;

    while (currentId) {
      const { data } = await supabase
        .from('document_folders')
        .select('name, parent_folder_id')
        .eq('id', currentId)
        .single();

      if (data) {
        pathParts.unshift(data.name);
        currentId = data.parent_folder_id;
      } else {
        break;
      }
    }

    return pathParts.join(' / ');
  };

  const filteredFolders = isSearching ? searchResults.folders : folders.filter(f => 
    f.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredDocuments = isSearching ? searchResults.documents : documents.filter(d => 
    d.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Company Documents</h1>
            <p className="text-slate-600 mt-1">Manage your files and folders</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowCreateFolderModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
            >
              <FolderPlus className="w-5 h-5" />
              New Folder
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            >
              <Upload className="w-5 h-5" />
              Upload Files
            </button>
          </div>
        </div>

        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 text-sm">
          {breadcrumbs.map((crumb, idx) => (
            <div key={idx} className="flex items-center gap-2">
              {idx > 0 && <ChevronRight className="w-4 h-4 text-slate-400" />}
              <button
                onClick={() => navigateToFolder(crumb.id, crumb.name)}
                className={`${
                  idx === breadcrumbs.length - 1
                    ? 'text-blue-600 font-semibold'
                    : 'text-slate-600 hover:text-blue-600'
                } transition-colors`}
              >
                {idx === 0 && <Home className="w-4 h-4 inline mr-1" />}
                {crumb.name}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Search and View Toggle */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              title="Grid View"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              title="List View"
            >
              <List className="w-4 h-4" />
            </button>
          </div>

          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search files and folders..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              searchGlobally(e.target.value);
            }}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div 
        className={`relative bg-white rounded-xl shadow-sm border-2 p-6 transition-all ${
          isDraggingOver 
            ? 'border-blue-500 border-dashed bg-blue-50' 
            : 'border-slate-200'
        }`}
        onDragEnter={handleMainAreaDragEnter}
        onDragLeave={handleMainAreaDragLeave}
        onDragOver={handleMainAreaDragOver}
        onDrop={handleMainAreaDrop}
      >
        {isDraggingOver && (
          <div className="absolute inset-0 flex items-center justify-center bg-blue-50 bg-opacity-90 rounded-xl z-10 pointer-events-none">
            <div className="text-center">
              <Upload className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-bounce" />
              <p className="text-xl font-semibold text-blue-600">Drop files to upload</p>
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Bulk Actions Toolbar - Fixed inside content */}
            {(filteredFolders.length > 0 || filteredDocuments.length > 0) && (
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200">
                <button
                  onClick={toggleSelectAll}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  {selectedFolders.size === filteredFolders.length && selectedDocuments.size === filteredDocuments.length && filteredFolders.length > 0 && filteredDocuments.length > 0 ? (
                    <CheckSquare className="w-4 h-4" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  Select All
                </button>
                
                {(selectedFolders.size > 0 || selectedDocuments.size > 0) && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-700 mr-2">
                      {selectedFolders.size + selectedDocuments.size} selected
                    </span>
                    <button
                      onClick={() => {
                        setSelectedFolders(new Set());
                        setSelectedDocuments(new Set());
                      }}
                      className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => {
                        loadAllFolders();
                        setShowMoveModal(true);
                      }}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5"
                    >
                      <FolderPlus className="w-4 h-4" />
                      Move
                    </button>
                    <button
                      onClick={() => setShowBulkDeleteConfirm(true)}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1.5"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Folders */}
            {filteredFolders.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-600 uppercase mb-3">Folders</h3>
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {filteredFolders.map((folder) => {
                    const isAccessible = folder.is_accessible !== false;
                    const canAccess = isAccessible || profile?.role === 'Superadmin' || profile?.role === 'Admin';
                    const isGrayedOut = !isAccessible && profile?.role !== 'Superadmin' && profile?.role !== 'Admin';
                    
                    return (
                      <div
                        key={folder.id}
                        className={`group relative border rounded-lg p-4 transition-all ${
                          isGrayedOut
                            ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                            : 'border-slate-200 hover:border-blue-500 hover:shadow-md cursor-pointer'
                        }`}
                      >
                        {/* Checkbox */}
                        <div
                          className="absolute top-2 left-2 z-10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => {
                              const newSelected = new Set(selectedFolders);
                              if (newSelected.has(folder.id)) {
                                newSelected.delete(folder.id);
                              } else {
                                newSelected.add(folder.id);
                              }
                              setSelectedFolders(newSelected);
                            }}
                            className="p-1 bg-white rounded border border-slate-300 hover:bg-slate-50 transition-colors"
                          >
                            {selectedFolders.has(folder.id) ? (
                              <CheckSquare className="w-4 h-4 text-blue-600" />
                            ) : (
                              <Square className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                        </div>

                        <div
                          onClick={() => canAccess && navigateToFolder(folder.id, folder.name)}
                          className="flex flex-col items-center relative"
                        >
                          <div className="relative mb-3">
                            <Folder className={`w-12 h-12 ${
                              isGrayedOut ? 'text-slate-400' : 'text-blue-500'
                            }`} />
                            {isGrayedOut && (
                              <div className="absolute -top-1 -right-1 bg-slate-600 rounded-full p-1">
                                <Lock className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </div>
                          <span className={`text-sm font-medium text-center w-full break-words px-2 leading-tight ${
                            isGrayedOut ? 'text-slate-500' : 'text-slate-900'
                          }`} title={folder.name}>
                            {folder.name}
                          </span>
                          {isGrayedOut && (
                            <span className="text-xs text-slate-400 mt-1 text-center">
                              No access
                            </span>
                          )}
                          {isSearching && 'path' in folder && (
                            <span className="text-xs text-slate-500 mt-1 text-center truncate w-full">
                              {folder.path}
                            </span>
                          )}
                        </div>
                        {((profile?.role === 'Superadmin') || (!folder.is_system_folder && (profile?.role === 'Admin'))) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteFolder(folder.id, folder.is_system_folder);
                            }}
                            className="absolute top-2 right-2 p-1.5 bg-white rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-50 transition-all"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 text-left">
                        <th className="pb-2 px-3 text-xs font-semibold text-slate-600 uppercase">Name</th>
                        <th className="pb-2 px-3 text-xs font-semibold text-slate-600 uppercase">Date Modified</th>
                        <th className="pb-2 px-3 text-xs font-semibold text-slate-600 uppercase">Kind</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFolders.map((folder) => {
                        const isAccessible = folder.is_accessible !== false;
                        const canAccess = isAccessible || profile?.role === 'Superadmin' || profile?.role === 'Admin';
                        
                        return (
                          <tr
                            key={folder.id}
                            onClick={() => canAccess && navigateToFolder(folder.id, folder.name)}
                            className={`border-b border-slate-100 hover:bg-blue-50 ${
                              canAccess ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
                            }`}
                          >
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2">
                                <Folder className="w-5 h-5 text-blue-600 flex-shrink-0" />
                                <span className="text-sm font-medium text-slate-900">{folder.name}</span>
                                {!isAccessible && (
                                  <Lock className="w-3 h-3 text-slate-400" />
                                )}
                              </div>
                            </td>
                            <td className="py-2 px-3 text-sm text-slate-600">
                              {new Date(folder.created_at).toLocaleDateString()}
                            </td>
                            <td className="py-2 px-3 text-sm text-slate-600">Folder</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Files */}
            {filteredDocuments.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-600 uppercase mb-3">Files</h3>
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {filteredDocuments.map((doc) => {
                    const Icon = getFileIcon(doc.mime_type);
                    const isImage = doc.mime_type?.startsWith('image/') || 
                                    /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(doc.name);
                    const isPdf = doc.mime_type === 'application/pdf' || doc.name.toLowerCase().endsWith('.pdf');
                    
                    return (
                      <div
                        key={doc.id}
                        onClick={() => {
                          setPreviewDocument(doc);
                          setShowPreview(true);
                        }}
                        className={viewMode === 'grid' 
                          ? "relative bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200 hover:border-blue-400 hover:shadow-lg transition-all duration-200 h-32 flex flex-col items-center justify-center cursor-pointer"
                          : "flex items-center gap-3 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all w-full"
                        }
                      >
                        {/* Checkbox */}
                        <div
                          className="absolute top-2 left-2 z-10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => {
                              const newSelected = new Set(selectedDocuments);
                              if (newSelected.has(doc.id)) {
                                newSelected.delete(doc.id);
                              } else {
                                newSelected.add(doc.id);
                              }
                              setSelectedDocuments(newSelected);
                            }}
                            className="p-1 bg-white rounded border border-slate-300 hover:bg-slate-50 transition-colors"
                          >
                            {selectedDocuments.has(doc.id) ? (
                              <CheckSquare className="w-4 h-4 text-blue-600" />
                            ) : (
                              <Square className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                        </div>

                        <div className="flex flex-col items-center w-full">
                          {isImage || isPdf ? (
                            <div className={`w-full h-16 mb-1 rounded-lg overflow-hidden flex items-center justify-center ${
                              isPdf ? 'bg-white border border-red-200' : 'bg-slate-100'
                            }`}>
                              {isPdf ? (
                                <PDFThumbnail doc={doc} />
                              ) : (
                                <DocumentImage doc={doc} className="max-w-full max-h-full object-contain" />
                              )}
                            </div>
                          ) : (
                            <Icon className="w-10 h-10 text-slate-600 mb-2" />
                          )}
                          <span className="text-xs font-medium text-slate-900 text-center w-full break-words px-1 leading-tight" title={doc.name}>
                            {doc.name}
                          </span>
                          {isSearching && 'path' in doc && (
                            <span className="text-xs text-slate-500 mb-1 text-center truncate w-full">
                              {doc.path}
                            </span>
                          )}
                          {doc.file_size > 0 && (
                            <span className="text-xs text-slate-500">
                              {formatFileSize(doc.file_size)}
                            </span>
                          )}
                        </div>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1 transition-all">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewDocument(doc);
                              setShowPreview(true);
                            }}
                            className="p-1.5 bg-white rounded-lg hover:bg-blue-50 transition-all"
                          >
                            <Eye className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadDocument(doc);
                            }}
                            className="p-1.5 bg-white rounded-lg hover:bg-green-50 transition-all"
                          >
                            <Download className="w-4 h-4 text-green-600" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteDocument(doc);
                            }}
                            className="p-1.5 bg-white rounded-lg hover:bg-red-50 transition-all"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 text-left">
                        <th className="pb-2 px-3 text-xs font-semibold text-slate-600 uppercase">Name</th>
                        <th className="pb-2 px-3 text-xs font-semibold text-slate-600 uppercase">Date Modified</th>
                        <th className="pb-2 px-3 text-xs font-semibold text-slate-600 uppercase">Size</th>
                        <th className="pb-2 px-3 text-xs font-semibold text-slate-600 uppercase">Kind</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDocuments.map((doc) => {
                        const Icon = getFileIcon(doc.mime_type);
                        
                        return (
                          <tr
                            key={doc.id}
                            onClick={() => {
                              setPreviewDocument(doc);
                              setShowPreview(true);
                            }}
                            className="border-b border-slate-100 hover:bg-blue-50 cursor-pointer"
                          >
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-2">
                                <Icon className="w-5 h-5 text-slate-600 flex-shrink-0" />
                                <span className="text-sm font-medium text-slate-900">{doc.name}</span>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-sm text-slate-600">
                              {new Date(doc.created_at).toLocaleDateString()}
                            </td>
                            <td className="py-2 px-3 text-sm text-slate-600">
                              {formatFileSize(doc.file_size)}
                            </td>
                            <td className="py-2 px-3 text-sm text-slate-600">
                              {doc.file_type || doc.mime_type?.split('/')[1]?.toUpperCase() || 'File'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Empty State */}
            {filteredFolders.length === 0 && filteredDocuments.length === 0 && (
              <div className="text-center py-12">
                <Folder className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {searchTerm ? 'No results found' : 'This folder is empty'}
                </h3>
                <p className="text-slate-600 mb-6">
                  {searchTerm
                    ? 'Try adjusting your search terms'
                    : 'Create a new folder or upload files to get started'}
                </p>
                {!searchTerm && (
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={() => setShowCreateFolderModal(true)}
                      className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
                    >
                      <FolderPlus className="w-5 h-5" />
                      New Folder
                    </button>
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Upload className="w-5 h-5" />
                      Upload Files
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateFolderModal && (
        <CreateFolderModal
          currentFolderId={currentFolderId}
          onClose={() => setShowCreateFolderModal(false)}
          onSuccess={() => {
            setShowCreateFolderModal(false);
            loadFoldersAndDocuments();
          }}
        />
      )}
      
      {showUploadModal && (
        <UploadDocumentModal
          currentFolderId={currentFolderId}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            loadFoldersAndDocuments();
          }}
        />
      )}
      
      {showPreview && previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          onClose={() => {
            setShowPreview(false);
            setPreviewDocument(null);
          }}
        />
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Confirm Bulk Delete</h3>
            <p className="text-slate-600 mb-6">
              Are you sure you want to delete {selectedFolders.size + selectedDocuments.size} item(s)?
              {selectedFolders.size > 0 && ` (${selectedFolders.size} folder(s))`}
              {selectedDocuments.size > 0 && ` (${selectedDocuments.size} file(s))`}
              <br /><br />
              <strong className="text-red-600">This action cannot be undone.</strong>
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Modal */}
      {showMoveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Move Items</h3>
            <p className="text-slate-600 mb-4">
              Select a destination folder for {selectedFolders.size + selectedDocuments.size} item(s):
            </p>
            
            <div className="mb-6 max-h-96 overflow-y-auto border border-slate-200 rounded-lg">
              {/* Root folder option */}
              <button
                onClick={() => setMoveTargetFolderId('root')}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-200 ${
                  moveTargetFolderId === 'root' ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  <Home className="w-4 h-4 text-slate-600" />
                  <span className="font-medium">My Documents (Root)</span>
                </div>
              </button>
              
              {/* Hierarchical folder tree */}
              {(() => {
                const renderFolderTree = (parentId: string | null, level: number = 0) => {
                  const childFolders = availableFolders.filter(
                    folder => folder.parent_folder_id === parentId && !selectedFolders.has(folder.id)
                  );
                  
                  return childFolders.map((folder) => {
                    const hasChildren = availableFolders.some(
                      f => f.parent_folder_id === folder.id && !selectedFolders.has(f.id)
                    );
                    const isExpanded = expandedFolders.has(folder.id);
                    
                    return (
                      <div key={folder.id}>
                        <div
                          className={`w-full text-left hover:bg-slate-50 transition-colors border-b border-slate-200 ${
                            moveTargetFolderId === folder.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                          }`}
                          style={{ paddingLeft: `${(level + 1) * 16 + 16}px` }}
                        >
                          <div className="flex items-center gap-2 py-3">
                            {hasChildren && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const newExpanded = new Set(expandedFolders);
                                  if (isExpanded) {
                                    newExpanded.delete(folder.id);
                                  } else {
                                    newExpanded.add(folder.id);
                                  }
                                  setExpandedFolders(newExpanded);
                                }}
                                className="p-0.5 hover:bg-slate-200 rounded"
                              >
                                <ChevronRight className={`w-4 h-4 text-slate-600 transition-transform ${
                                  isExpanded ? 'rotate-90' : ''
                                }`} />
                              </button>
                            )}
                            {!hasChildren && <div className="w-5" />}
                            <button
                              onClick={() => setMoveTargetFolderId(folder.id)}
                              className="flex items-center gap-2 flex-1"
                            >
                              <Folder className="w-4 h-4 text-blue-500" />
                              <span className="font-medium">{folder.name}</span>
                            </button>
                          </div>
                        </div>
                        {isExpanded && renderFolderTree(folder.id, level + 1)}
                      </div>
                    );
                  });
                };
                
                return renderFolderTree(null);
              })()}
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowMoveModal(false);
                  setMoveTargetFolderId(null);
                }}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkMove}
                disabled={!moveTargetFolderId}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Move Here
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateFolderModal({
  currentFolderId,
  onClose,
  onSuccess,
}: {
  currentFolderId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [folderName, setFolderName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!folderName.trim()) return;

    setLoading(true);
    try {
      const { error } = await supabase.from('document_folders').insert({
        name: folderName.trim(),
        parent_folder_id: currentFolderId,
      });

      if (error) throw error;
      onSuccess();
    } catch (error: any) {
      console.error('Error creating folder:', error);
      if (error.code === '23505') {
        alert('A folder with this name already exists in this location');
      } else {
        alert('Failed to create folder');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900">Create New Folder</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Folder Name *
            </label>
            <input
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Enter folder name..."
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              autoFocus
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-slate-300 rounded-lg hover:bg-slate-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !folderName.trim()}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-semibold"
            >
              {loading ? 'Creating...' : 'Create Folder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UploadDocumentModal({
  currentFolderId,
  onClose,
  onSuccess,
}: {
  currentFolderId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { user } = useAuth();
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      setFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = currentFolderId ? `${currentFolderId}/${fileName}` : fileName;

        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from('company-documents')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        // Save metadata to database
        const { error: dbError } = await supabase.from('documents').insert({
          name: file.name,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          file_type: fileExt || 'unknown',
          mime_type: file.type,
          folder_id: currentFolderId,
          uploaded_by: user?.id,
        });

        if (dbError) throw dbError;
        
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }

      onSuccess();
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload files');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900">Upload Files</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-all cursor-pointer ${
              isDragging 
                ? 'border-blue-500 bg-blue-50 scale-105' 
                : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
            }`}
          >
            <Upload className={`w-16 h-16 mx-auto mb-4 transition-colors ${
              isDragging ? 'text-blue-600' : 'text-slate-400'
            }`} />
            <p className="text-lg text-slate-600 mb-2">
              {isDragging ? (
                <span className="text-blue-600 font-semibold">Drop files here</span>
              ) : (
                <>
                  <span className="text-blue-600 font-semibold">Click to upload</span> or drag and drop
                </>
              )}
            </p>
            <p className="text-sm text-slate-500">PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, GIF (max 50MB per file)</p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {files.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold text-slate-900">Selected Files ({files.length})</h3>
              <div className="max-h-40 overflow-y-auto space-y-2">
                {files.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-slate-600" />
                      <div>
                        <p className="text-sm font-medium text-slate-900">{file.name}</p>
                        <p className="text-xs text-slate-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFiles(files.filter((_, i) => i !== idx))}
                      className="p-1 hover:bg-slate-200 rounded"
                    >
                      <X className="w-4 h-4 text-slate-600" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {uploading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Uploading...</span>
                <span className="text-blue-600 font-semibold">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              className="flex-1 px-6 py-3 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || files.length === 0}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-semibold"
            >
              {uploading ? 'Uploading...' : `Upload ${files.length} File${files.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DocumentPreviewModal({
  document,
  onClose,
}: {
  document: Document;
  onClose: () => void;
}) {
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPreview();
  }, [document]);

  const loadPreview = async () => {
    try {
      // Check if file_path is an external URL (from GoHighLevel)
      if (document.file_path.startsWith('http://') || document.file_path.startsWith('https://')) {
        // External URL - use directly
        setPreviewUrl(document.file_path);
      } else {
        // Supabase storage path - download from storage
        const { data, error } = await supabase.storage
          .from('company-documents')
          .download(document.file_path);

        if (error) throw error;

        const url = URL.createObjectURL(data);
        setPreviewUrl(url);
      }
    } catch (error) {
      console.error('Error loading preview:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if it's an image by mime_type, file_type, or file extension
  const isImage = document.mime_type?.startsWith('image/') || 
                  document.file_type?.toLowerCase() === 'image' ||
                  /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(document.name);
  const isPdf = document.mime_type === 'application/pdf' || document.name.toLowerCase().endsWith('.pdf');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{document.name}</h2>
            <p className="text-sm text-slate-600 mt-1">
              {formatFileSize(document.file_size)} • {document.file_type.toUpperCase()}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6 bg-slate-50">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : previewUrl && isImage ? (
            <img src={previewUrl} alt={document.name} className="max-w-full h-auto mx-auto rounded-lg" />
          ) : previewUrl && isPdf ? (
            <iframe src={previewUrl} className="w-full h-full min-h-[600px]" />
          ) : (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Preview not available for this file type</p>
              <p className="text-sm text-slate-500 mt-2">Download the file to view it</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
